import type { FollowUp } from '@paintscout/api'
import type { QuoteDocument } from 'paintscout'
import moment from 'moment'

export type FollowUpScheduleStatus = 'sent' | 'scheduled' | 'notApplicable' | 'pending'

export interface FollowUpScheduleInfo {
  /**
   * The display status for an un-sent follow-up, matching what the Follow-Ups
   * dialog renders:
   * - `sent`          this follow-up has already been sent
   * - `scheduled`     it will fire — shows "Scheduled - <dueDate>"
   * - `notApplicable` the quote no longer meets the criteria — shows "Not applicable to send"
   * - `pending`       the quote hasn't been sent yet, so no date can be computed
   */
  status: FollowUpScheduleStatus
  hasBeenSent: boolean
  notStatusHappened: boolean
  fromStatusHasHappened: boolean
  overdueFollowUp: boolean
  dueDate: number
}

/**
 * Compute the schedule/applicability status for a single follow-up against a
 * quote. This is the single source of truth shared by the Follow-Ups dialog
 * (FollowUpTable/FollowUpSummary), the sidebar follow-up count, and the
 * get_follow_ups LLM tool, so they can never disagree about whether a follow-up
 * is "Scheduled" vs "Not applicable to send" (#20841).
 *
 * The derived status mirrors FollowUpSummary's render logic: the send-status row
 * only appears once the quote has been sent, and within that it shows a date only
 * when the trigger (fromStatus) has occurred, the "not" status has NOT occurred,
 * and the computed due date is still in the future.
 */
export function getFollowUpScheduleInfo({
  quote,
  followUp
}: {
  quote?: Partial<QuoteDocument>
  followUp: FollowUp
}): FollowUpScheduleInfo {
  const quoteSentDate = quote?.dates?.client?.sent ?? null
  const quoteViewedDate = quote?.dates?.client?.viewed ?? null
  const quoteExpiryDate = quote?.dates?.expiryDate ?? null
  const historyKeys = Object.keys(quote?.status?.history ?? {})

  const notStatus: string[] =
    followUp.conditions.notStatus === 'responded'
      ? quote?.is_invoice
        ? ['paid', 'partial']
        : ['accepted', 'declined', 'onHold']
      : ['viewed']

  const notStatusHappened =
    followUp.conditions.fromStatus !== 'overdue' && notStatus.some((status) => historyKeys.includes(status))

  const fromStatusHasHappened =
    followUp.conditions.fromStatus === 'overdue'
      ? Boolean(quoteExpiryDate) && moment(quoteExpiryDate).isBefore(Date.now())
      : historyKeys.includes(followUp.conditions.fromStatus)

  // A follow-up can be due from 3 different start points (overdue / sent / viewed).
  const quoteOverdue =
    followUp.conditions.fromStatus === 'overdue' && quoteExpiryDate && moment(quoteExpiryDate).isBefore(Date.now())
  const overdueDueDate =
    quoteOverdue &&
    moment(quoteExpiryDate).add(followUp.conditions.days, 'days').add(followUp.conditions.months, 'months').unix() *
      1000

  const sentDueDate =
    followUp.conditions?.interval ||
    moment(quoteSentDate).add(followUp.conditions.days, 'days').add(followUp.conditions.months, 'months').unix() * 1000

  const viewDueDate =
    followUp.conditions?.interval ||
    moment(quoteViewedDate).add(followUp.conditions.days, 'days').add(followUp.conditions.months, 'months').unix() *
      1000

  const hasBeenSent = Boolean(quote?.followUps && (quote?.followUps[followUp.key]?.sent ?? false))

  const dueDate = overdueDueDate
    ? overdueDueDate
    : followUp.conditions.fromStatus === 'sent'
      ? sentDueDate
      : viewDueDate

  const overdueFollowUp = dueDate < Date.now() && !hasBeenSent

  // Mirror FollowUpSummary: nothing is shown until the quote has been sent at least once.
  const quoteHasBeenSent = historyKeys.includes('sent')

  let status: FollowUpScheduleStatus
  if (!quoteHasBeenSent) {
    status = 'pending'
  } else if (hasBeenSent) {
    status = 'sent'
  } else if (!notStatusHappened && fromStatusHasHappened && !overdueFollowUp) {
    status = 'scheduled'
  } else {
    status = 'notApplicable'
  }

  return {
    status,
    hasBeenSent,
    notStatusHappened,
    fromStatusHasHappened,
    overdueFollowUp,
    dueDate
  }
}
